import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupHoverOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('pointerover', this.#onCategoryPointerOver);
    this.removeEventListener('pointerout', this.#onCategoryPointerOut);
    clearTimeout(this.#hoverTimer);
  }

  /**
   * Hover-to-open for the desktop drawer navigation (GLOSS-74 item 3).
   *
   * Opt-in through the header's "Open panels on hover" setting, which the
   * section renders as `data-drawer-hover-open` on <header-component>. Only
   * a mouse on a large screen qualifies: touch and pen pointers, keyboard
   * users and small screens keep the click-to-open behaviour, and the
   * hamburger itself is never hover-driven. A short intent delay stops a
   * pointer crossing the rail from flipping panels on its way somewhere else.
   * Panels never close on hover-out — a category stays open until another is
   * opened or the drawer is closed, matching the click behaviour.
   */
  #hoverTimer;
  #hoverMedia = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');

  #setupHoverOpen() {
    const header = this.closest('header-component');
    if (!header?.hasAttribute('data-drawer-hover-open') || !header.classList.contains('header--drawer-nav')) return;

    this.addEventListener('pointerover', this.#onCategoryPointerOver);
    this.addEventListener('pointerout', this.#onCategoryPointerOut);
  }

  /**
   * @param {Event} event
   * @returns {HTMLDetailsElement | null} The category whose rail row the pointer is over
   */
  #hoverCategoryFor(event) {
    if (!(event.target instanceof Element)) return null;
    const summary = event.target.closest('details[name="menu-drawer-category"] > summary');
    const details = summary?.parentElement;
    return details instanceof HTMLDetailsElement ? details : null;
  }

  /** @param {PointerEvent} event */
  #onCategoryPointerOver = (event) => {
    if (event.pointerType !== 'mouse' || !this.#hoverMedia.matches || !this.isOpen) return;
    const details = this.#hoverCategoryFor(event);
    if (!details || details.open) return;

    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = setTimeout(() => this.#hoverOpen(details), 120);
  };

  /** @param {PointerEvent} event */
  #onCategoryPointerOut = (event) => {
    if (!this.#hoverCategoryFor(event)) return;
    clearTimeout(this.#hoverTimer);
  };

  /**
   * Opens a category the way a click would, minus focus handling: the focus
   * trap already spans the whole drawer in the drawer-nav layout (see open()),
   * and moving focus on hover would yank it away from whatever the shopper
   * is doing. Setting `open` lets the native exclusive accordion (the `name`
   * attribute) close the previously open category, and the resulting toggle
   * events run syncCategoryState for aria, panel classes and promo strips.
   * @param {HTMLDetailsElement} details
   */
  #hoverOpen(details) {
    if (!this.isOpen || details.open || !details.isConnected) return;

    details.open = true;
    details.classList.add('menu-open');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
  }

  /**
   * Close on Escape, stepping outward (GLOSS-86): the nearest OPEN details
   * (an expanded category) closes first; otherwise the whole drawer closes.
   * `closest('details')` alone dead-ends when focus sits on a collapsed
   * category row — its own closed <details> matched and "closing" it was a
   * no-op, so Escape appeared to do nothing.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const openDetails = event.target instanceof Element ? event.target.closest('details[open]') : null;
    const target = openDetails instanceof HTMLDetailsElement ? openDetails : this.refs.details;
    this.#close(target);

    // Keyboard continuity: closing the whole drawer hands focus back to the
    // toggle it opened from; closing a category hands it to that category's
    // own summary (collapsing the panel would otherwise drop the focused
    // link out of the render tree and strand focus at <body>).
    const toggle = target.querySelector('summary');
    if (toggle instanceof HTMLElement) toggle.focus();
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Reconciles a category's state when its <details> toggles for any reason —
   * including the browser's native exclusive-accordion close (the `name`
   * attribute on category details), which bypasses open()/#close().
   * @param {Event} event
   */
  syncCategoryState(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;

    const summary = details.querySelector('summary');
    summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');

    if (!details.open) {
      details.classList.remove('menu-open');
    }

    const anyCategoryOpen = this.refs.menuDrawer.querySelector('details[name="menu-drawer-category"][open]') !== null;
    this.refs.menuDrawer.classList.toggle('menu-drawer--has-submenu-opened', anyCategoryOpen);

    // Activate the promo strip that matches the open category. Category
    // summary ids embed the menu link's handle (HeaderDrawer-<handle>), and
    // strips carry the same handle in data-category; native toggle fires on
    // every open/close/switch/reset path, so this stays reconciled.
    const openSummary = this.refs.menuDrawer.querySelector('details[name="menu-drawer-category"][open] > summary');
    const openHandle = openSummary ? openSummary.id.replace('HeaderDrawer-', '') : '';
    this.refs.menuDrawer.querySelectorAll('.nav-promo-strip').forEach((strip) => {
      const matches = openHandle !== '' && strip instanceof HTMLElement && strip.dataset.category === openHandle;
      strip.classList.toggle('nav-promo-strip--active', matches);
    });
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      // A click on an already-open category's summary natively closes it by
      // this point — the toggle handler (syncCategoryState) owns that path,
      // so don't re-apply open-state classes to a details that just closed.
      if (details instanceof HTMLDetailsElement && !details.open) return;

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

      // In the drawer-nav layout the trap covers the whole drawer: on desktop
      // the rail, the open category's panel, the drawer's own band, and the
      // promo strip are one surface; a category-scoped trap strands the band,
      // strip, and other rail categories outside it. On mobile the same wide
      // trap is safe because the CSS hides (visibility) the root content the
      // open submenu covers, so hidden elements drop out of the boundary
      // computation — only the submenu and the strip remain focusable.
      const isDrawerNav = this.closest('.header--drawer-nav') !== null;
      const trapTarget = isDrawerNav ? this.refs.details : details;
      onAnimationEnd(drawer || details, () => trapFocus(trapTarget), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
